import * as Sentry from "@sentry/react";
import { DashboardIcon, RefreshIcon, WarningIcon } from "@xola/icons";
import { Alert, Button } from "@xola/ui-kit";
import clsx from "clsx";
import React, { ReactNode } from "react";
import { useHistory } from "react-router-dom";
import { UserRole } from "types";
import { queryClient } from "../api/queryClient";
import { AccountState } from "../hooks/useAccount";
import { NoTranslate } from "./Common/NoTranslate";

const isProduction = import.meta.env.PROD;

const DefaultFallbackComponent = ({ error, componentStack }: any) => {
    const user = queryClient.getQueryData<AccountState>(["accounts"])?.user;
    const isAdmin = user?.roles.includes(UserRole.ADMIN);
    const isLocalhost = window.location.hostname === "localhost";

    return (
        <NoTranslate as="div" className="space-y-8 p-5 text-center">
            <Alert className="mb-5" color="danger">
                <p>Whoops, something went wrong... Don&apos;t worry, we are probably already working on it!</p>
                <p>While you are here, you can check the console for more info.</p>
            </Alert>

            {(isAdmin || isLocalhost) && <ErrorForAdmin error={error.toString()} componentStack={componentStack} />}
            {/* <>{isAdmin && (true && <DebugFallbackComponent error={error} componentStack={componentStack} />)}</> */}

            <ActionButtons />
        </NoTranslate>
    );
};

const ErrorForAdmin = ({ error, componentStack }: any) => {
    return (
        <NoTranslate as="div" className="space-y-1 py-6 text-left">
            <em>Because you are a Xola admin, you can see the following additional details:</em>
            <div className="rounded bg-gray-lighter p-3 font-mono text-sm">{error.toString()}</div>
            <StackTrace stack={componentStack} />
        </NoTranslate>
    );
};

const DebugFallbackComponent = ({ error, componentStack }: any) => {
    const message = error?.toString();

    const filepathMatches = componentStack.match(/src.*(js|tsx?)/i) ?? [];
    const filename = filepathMatches[0] ?? "";

    const lineMatches = componentStack.match(/:(\d+):(\d+)/i) ?? [];
    const [, lineNumber, charPos] = lineMatches;

    return (
        <NoTranslate as="div" className="space-y-6 p-5 text-center">
            <div className="space-y-4 text-left">
                <h1 className="my-3">
                    <WarningIcon size="large" className="!-top-0.5 mr-2 text-danger" /> Fatal Error
                </h1>
                <p className="my-3">The following Javascript error has occurred, details in your console</p>
                <Alert color="danger" className="px-3 text-left font-mono">
                    <div>
                        <span className="font-semibold">{message}</span> at {filename} ({lineNumber}:{charPos})
                    </div>
                </Alert>

                <StackTrace stack={componentStack} start={1} />
            </div>

            <ActionButtons />
        </NoTranslate>
    );
};

const PrimaryComponent = isProduction ? DefaultFallbackComponent : DebugFallbackComponent;

interface ErrorBoundaryProps {
    fallbackMessage?: string;
    fallbackComponent?: React.ReactNode;
    children: ReactNode;
}

export const ErrorBoundary = ({ fallbackComponent, fallbackMessage, children }: ErrorBoundaryProps) => {
    const fallback = getFallback(fallbackComponent ?? PrimaryComponent, fallbackMessage);

    return <Sentry.ErrorBoundary fallback={fallback}>{children}</Sentry.ErrorBoundary>;
};

const getFallback = (fallbackComponent: React.ReactNode, fallbackMessage?: string) => {
    if (fallbackMessage) {
        return <div className="error-boundary w-full p-2 text-gray-dark">{fallbackMessage}</div>;
    }

    return fallbackComponent as React.ReactElement;
};

export const StackTrace = ({ stack, start = 0 }: { stack: string; start?: number }) => {
    const trace = stack
        .split("\n")
        .slice(start)
        .map((message: string, index: number) => {
            const line = message.trim();
            if (/at (errorboundary|div)/i.test(line)) {
                return null;
            }

            return (
                // eslint-disable-next-line react/no-array-index-key
                <div key={`${index}-${line}`} className={clsx({ "opacity-40": line.includes("node_modules") })}>
                    {line.trim()}
                </div>
            );
        })
        .filter(Boolean)
        .slice(0, 6);

    return (
        <Alert color="secondary" className="whitespace-pre-wrap px-5 py-3 font-mono !text-sm leading-6">
            {trace}
        </Alert>
    );
};

const ActionButtons = () => {
    const history = useHistory();
    const handleClick = () => history.push("/");

    return (
        <div className="space-x-8">
            <Button icon={<RefreshIcon />} onClick={() => window.location.reload()}>
                Reload App
            </Button>
            <Button icon={<DashboardIcon />} onClick={handleClick}>
                Go Home
            </Button>
        </div>
    );
};
